import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useClerkClientAuth } from '@client/hooks/useClerkClientAuth';
import {
  Loader2,
  User,
  Building2,
  AlertCircle,
  Info,
  Clipboard,
  ClipboardCheck,
  ClipboardList,
} from 'lucide-react';
import { builtEnvironment, isRunningLocal, SessionType, type AdvisorBrandingMode } from '@shared';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { WaterlilyLogo } from '../components/WaterlilyLogo';
import CompanyLogoContainer from '../components/CompanyLogo';
import { cn } from '@/lib/utils';
import { selectSession } from '@/model/selectors';
import { ClientLinkAdvisorBlock } from '@client/components/ClientLinkAdvisorBlock';
import { PhoneNumberField } from '@client/components/PhoneNumberField';
import {
  formatByCountry,
  stripDigits,
  validateForCountry,
  type SupportedCountryCode,
} from '@client/util/clerkAuthHelpers';
import { timeoutSignal } from '@client/util/timeoutSignal';

// API Gateway cuts every request off at 29 seconds, so after 30 no answer can still be coming.
// Submitting again is safe: the server finds the client the first attempt created and resumes its
// intake instead of creating a second client.
const CLIENT_INVITE_TIMEOUT_MS = 30_000;
const CLIENT_INVITE_TIMEOUT_MESSAGE =
  'This is taking longer than expected. Please check your connection and try again.';
const CLIENT_INVITE_NETWORK_MESSAGE =
  'We could not connect to Waterlily. Please check your connection and try again.';

interface AdvisorPublicProfile {
  advisorId: string;
  advisorFirstName: string;
  advisorLastName: string;
  organizationDisplayName: string | null;
  organizationName: string | null;
  advisorLogoKey: string | null;
  whitelabelBrandingPreference: WhitelabelBrandingPreference;
  brandingMode: AdvisorBrandingMode;
  consumerSmsMfaRequired: boolean;
}

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  phoneCountryCode: SupportedCountryCode;
  phoneNumber: string;
}

interface FormErrors {
  firstName?: string;
  lastName?: string;
  email?: string;
  phoneNumber?: string;
}

interface ClientInviteResponse {
  redirectUrl: string;
  requiresSignIn?: boolean;
}

export function AdvisorPublicInvite() {
  const { advisorId } = useParams<{ advisorId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isClerkSignedIn: isAuthenticated, isClerkLoading: authLoading } = useClerkClientAuth();
  const session = useSelector(selectSession);

  const [advisor, setAdvisor] = useState<AdvisorPublicProfile | null>(null);
  const [isLoadingAdvisor, setIsLoadingAdvisor] = useState(true);
  const [advisorError, setAdvisorError] = useState<string | null>(null);

  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    email: '',
    phoneCountryCode: '+1',
    phoneNumber: '',
  });
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [signInUrl, setSignInUrl] = useState<string | null>(null);
  const [linkCopied, setLinkCopied] = useState(false);
  const [isPhoneTouched, setIsPhoneTouched] = useState(false);
  const hasExistingAssessmentError = Boolean(signInUrl);

  const env = isRunningLocal() ? 'dev' : builtEnvironment;

  const isAdvisorViewingLink =
    !authLoading &&
    isAuthenticated &&
    session.sessionType !== SessionType.unknown &&
    (session.sessionType === SessionType.advisor || session.sessionType === SessionType.wizard);

  const isEdjPilot = !!session.advisor?.featureFlags?.edjPilotAdvisor;
  // EDJ policy does not allow advisors to preview their reusable client link while signed in.
  const shouldBlockEdjAdvisor = isAdvisorViewingLink && isEdjPilot;
  // A role-bearing session without an advisor row cannot safely construct advisor navigation.
  const shouldBlockAdvisorWithoutRecord = isAdvisorViewingLink && !session.advisor?.advisorId;

  useEffect(() => {
    if (authLoading || isAdvisorViewingLink) {
      return;
    }

    if (isAuthenticated && session.sessionType !== SessionType.unknown) {
      if (session.sessionType === SessionType.client) {
        navigate('/client/home', { replace: true });
      } else if (
        session.sessionType !== SessionType.advisor &&
        session.sessionType !== SessionType.wizard
      ) {
        navigate('/', { replace: true });
      }
    }
  }, [isAuthenticated, authLoading, session.sessionType, isAdvisorViewingLink, navigate]);

  useEffect(() => {
    async function fetchAdvisorProfile() {
      if (!advisorId) {
        setAdvisorError('Invalid invitation link');
        setIsLoadingAdvisor(false);
        return;
      }

      try {
        const response = await fetch(
          `/${env}/api/public/advisor-public-profile/${encodeURIComponent(advisorId)}`,
        );

        if (!response.ok) {
          if (response.status === 404) {
            setAdvisorError('This advisor could not be found');
          } else {
            setAdvisorError('Unable to load advisor information');
          }
          return;
        }

        const data = await response.json();
        setAdvisor(data);
      } catch (error) {
        setAdvisorError('Unable to load advisor information. Please try again.');
      } finally {
        setIsLoadingAdvisor(false);
      }
    }

    fetchAdvisorProfile();
  }, [advisorId, env]);

  const validateForm = (): boolean => {
    const errors: FormErrors = {};

    if (!formData.firstName.trim()) {
      errors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      errors.lastName = 'Last name is required';
    }

    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        errors.email = 'Please enter a valid email address';
      }
    }

    if (advisor?.consumerSmsMfaRequired) {
      const phoneError = validateForCountry(
        stripDigits(formData.phoneNumber),
        formData.phoneCountryCode,
      );
      if (phoneError) {
        errors.phoneNumber = phoneError;
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm() || !advisorId) {
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);
    setSignInUrl(null);

    const signal = timeoutSignal(CLIENT_INVITE_TIMEOUT_MS);

    try {
      const hasAccessToResultsParam = searchParams.get('hasAccessToResults');
      const clientRelationshipStageParam = searchParams.get('clientRelationshipStage');
      const customReferralSourceIdParam = searchParams.get('customReferralSourceId');

      const body: Record<string, unknown> = {
        advisorId,
        clientFirstName: formData.firstName.trim(),
        clientLastName: formData.lastName.trim(),
        clientEmail: formData.email.trim().toLowerCase(),
      };
      if (hasAccessToResultsParam === 'true' || hasAccessToResultsParam === 'false') {
        body.hasAccessToResults = hasAccessToResultsParam === 'true';
      }
      if (clientRelationshipStageParam) {
        body.clientRelationshipStage = clientRelationshipStageParam;
      }
      if (customReferralSourceIdParam) {
        body.customReferralSourceId = customReferralSourceIdParam;
      }
      if (advisor?.consumerSmsMfaRequired) {
        body.consumerSmsMfa = {
          phoneNumber: `${formData.phoneCountryCode}${stripDigits(formData.phoneNumber)}`,
        };
      }

      const response = await fetch(`/${env}/api/public/client-invite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        signal,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.errorMessage || errorData.message || 'Unable to process your request',
        );
      }

      const data = (await response.json()) as ClientInviteResponse;

      if (data.redirectUrl) {
        if (data.requiresSignIn) {
          setSignInUrl(data.redirectUrl);
          return;
        }
        window.location.href = data.redirectUrl;
      } else {
        throw new Error('No redirect URL received');
      }
    } catch (error) {
      if (signal.aborted) {
        setSubmitError(CLIENT_INVITE_TIMEOUT_MESSAGE);
      } else if (error instanceof TypeError) {
        // fetch rejects with a TypeError when the network fails ("Load failed" in Safari).
        setSubmitError(CLIENT_INVITE_NETWORK_MESSAGE);
      } else {
        setSubmitError(
          error instanceof Error ? error.message : 'Something went wrong. Please try again.',
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: 'firstName' | 'lastName' | 'email', value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (field === 'email') {
      setSignInUrl(null);
    }
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const isMfaFormComplete =
    !advisor?.consumerSmsMfaRequired ||
    (formData.firstName.trim().length > 0 &&
      formData.lastName.trim().length > 0 &&
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim()) &&
      validateForCountry(stripDigits(formData.phoneNumber), formData.phoneCountryCode) === null);

  if (shouldBlockEdjAdvisor || shouldBlockAdvisorWithoutRecord) {
    return <ClientLinkAdvisorBlock advisorId={session.advisor?.advisorId} />;
  }

  if (isLoadingAdvisor || authLoading) {
    return (
      <div className="from-primary-50 to-primary-100 flex min-h-screen items-center justify-center bg-gradient-to-br via-white">
        <div className="text-center">
          <Loader2 className="text-primary mx-auto h-12 w-12 animate-spin" />
          <p className="mt-4 text-gray-700">Loading...</p>
        </div>
      </div>
    );
  }

  if (advisorError) {
    return (
      <div className="from-primary-50 to-primary-100 flex min-h-screen items-center justify-center bg-gradient-to-br via-white px-4">
        <div className="w-full max-w-md rounded-2xl bg-white p-8 text-center shadow-xl">
          <AlertCircle className="mx-auto h-16 w-16 text-red-700" />
          <h1 className="mt-4 text-xl font-semibold text-gray-900">Invalid Link</h1>
          <p className="mt-2 text-gray-700">{advisorError}</p>
          <Button
            className="mt-6 w-full rounded-full"
            onClick={() => (window.location.href = '/')}
          >
            Go to Home
          </Button>
        </div>
      </div>
    );
  }

  const advisorFullName = advisor ? `${advisor.advisorFirstName} ${advisor.advisorLastName}` : '';
  const organizationDisplay = advisor?.organizationDisplayName || advisor?.organizationName;

  if (isAdvisorViewingLink) {
    const advisorId = session.advisor!.advisorId;
    const currentUrl = window.location.href;

    return (
      <div className="from-primary-50 to-primary-100 flex min-h-screen flex-col bg-gradient-to-br via-white">
        <header className="flex items-center justify-start px-8 py-6">
          <WaterlilyLogo className="h-10 w-auto" />
        </header>

        <main className="flex flex-1 items-center justify-center px-4 pb-12">
          <div className="w-full max-w-lg">
            <div className="rounded-2xl bg-white p-8 shadow-lg">
              <div className="flex flex-col items-center text-center">
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-amber-100">
                  <Info className="h-8 w-8 text-amber-600" />
                </div>

                <h1 className="mt-5 text-2xl font-semibold text-gray-900">
                  This Link is for Clients
                </h1>

                <p className="mt-3 text-gray-700">
                  This link is for clients to complete their intake form. Since you're logged in as
                  an advisor, you can't open it as a client.
                </p>
              </div>

              <div className="mt-8 space-y-3">
                <Button
                  className="w-full rounded-full py-6 text-base font-medium"
                  onClick={() =>
                    navigate(
                      `/advisors/${advisorId}/clients?inviteClient=true&inviteClientType=fill-form`,
                    )
                  }
                >
                  <ClipboardList className="mr-2 h-5 w-5" />
                  Fill Out Intake Form for a Client
                </Button>

                <Button
                  variant="outline"
                  className="w-full rounded-full py-6 text-base font-medium"
                  onClick={() => navigate(`/advisors/${advisorId}/clients`)}
                >
                  Go to Dashboard
                </Button>
              </div>

              <div className="mt-6 rounded-xl bg-gray-50 p-4">
                <p className="text-sm font-medium text-gray-900">Ways to share this link</p>
                <ul className="mt-2 space-y-1.5 text-sm text-gray-700">
                  <li className="flex items-start gap-2">
                    <span className="mt-0.5 block h-1.5 w-1.5 shrink-0 rounded-full bg-gray-400" />
                    Send it to clients via text or email
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="mt-0.5 block h-1.5 w-1.5 shrink-0 rounded-full bg-gray-400" />
                    Embed it on your website or blog
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="mt-0.5 block h-1.5 w-1.5 shrink-0 rounded-full bg-gray-400" />
                    Add it to your email templates or CRM workflows
                  </li>
                </ul>
              </div>

              <div className="mt-4 rounded-xl bg-blue-50 p-4">
                <p className="text-sm font-medium text-blue-900">
                  Want to see what your clients see?
                </p>
                <p className="mt-1 text-sm text-blue-800">
                  {isEdjPilot
                    ? 'This link will not work while you are logged in as an advisor. To test it as a client would, copy the link, sign out of your advisor account, then paste it in your browser.'
                    : 'Open this link in an incognito/private window to preview the client experience.'}
                </p>
                <div className="mt-3 flex items-center gap-2 rounded-lg bg-white px-3 py-2">
                  <code className="flex-1 truncate text-xs text-gray-600">{currentUrl}</code>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="shrink-0 text-blue-700 hover:text-blue-900"
                    onClick={() => {
                      navigator.clipboard.writeText(currentUrl);
                      setLinkCopied(true);
                      setTimeout(() => setLinkCopied(false), 2000);
                    }}
                  >
                    {linkCopied ? (
                      <ClipboardCheck className="h-4 w-4" />
                    ) : (
                      <Clipboard className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </main>

        <footer className="py-4 text-center text-sm text-gray-700"></footer>
      </div>
    );
  }

  return (
    <div className="from-primary-50 to-primary-100 flex min-h-screen flex-col bg-gradient-to-br via-white">
      <header className="flex items-center justify-start px-8 py-6">
        <CompanyLogoContainer
          organizationDisplayName={advisor?.organizationDisplayName}
          advisorLogoKey={advisor?.advisorLogoKey}
          brandingPreference={advisor?.whitelabelBrandingPreference}
          brandingMode={advisor?.brandingMode}
          waterlilyFallback="logo"
        />
      </header>

      <main className="flex flex-1 items-center justify-center px-4 pb-12">
        <div className="w-full max-w-lg">
          <div className="mb-8 rounded-2xl bg-white p-6 shadow-lg">
            <div className="flex flex-col items-center text-center">
              <div className="bg-primary-100 flex h-20 w-20 items-center justify-center rounded-full">
                <User className="text-primary h-10 w-10" />
              </div>

              <p className="mt-4 text-sm font-medium text-gray-700">Your advisor</p>

              <h1 className="mt-1 text-2xl font-semibold text-gray-900">{advisorFullName}</h1>

              {organizationDisplay && (
                <div className="mt-2 flex items-center gap-2 text-gray-700">
                  <Building2 className="h-4 w-4" />
                  <span>{organizationDisplay}</span>
                </div>
              )}

              <p className="mt-4 max-w-xs text-gray-700">
                Begin your long-term care planning assessment with your advisor by entering your
                details below.
              </p>

              <p className="mt-3 max-w-xs text-xs text-gray-500">
                Your information is shared securely with your advisor.
              </p>
            </div>
          </div>

          <div className="rounded-2xl bg-white p-8 shadow-lg">
            <h2 className="mb-6 text-center text-lg font-medium text-gray-900">
              Enter Your Information
            </h2>

            <form
              onSubmit={handleSubmit}
              className="space-y-4"
            >
              <div>
                <label
                  htmlFor="firstName"
                  className="mb-1.5 block text-sm font-medium text-gray-700"
                >
                  First Name <span className="text-red-700">*</span>
                </label>
                <Input
                  id="firstName"
                  type="text"
                  placeholder="Enter your first name"
                  value={formData.firstName}
                  onChange={e => handleInputChange('firstName', e.target.value)}
                  className={cn(
                    formErrors.firstName && 'border-red-500 focus-visible:ring-red-500',
                  )}
                  disabled={isSubmitting}
                  autoComplete="given-name"
                />
                {formErrors.firstName && (
                  <p className="mt-1 text-sm text-red-700">{formErrors.firstName}</p>
                )}
              </div>

              <div>
                <label
                  htmlFor="lastName"
                  className="mb-1.5 block text-sm font-medium text-gray-700"
                >
                  Last Name <span className="text-red-700">*</span>
                </label>
                <Input
                  id="lastName"
                  type="text"
                  placeholder="Enter your last name"
                  value={formData.lastName}
                  onChange={e => handleInputChange('lastName', e.target.value)}
                  className={cn(formErrors.lastName && 'border-red-500 focus-visible:ring-red-500')}
                  disabled={isSubmitting}
                  autoComplete="family-name"
                />
                {formErrors.lastName && (
                  <p className="mt-1 text-sm text-red-700">{formErrors.lastName}</p>
                )}
              </div>

              <div>
                <label
                  htmlFor="email"
                  className="mb-1.5 block text-sm font-medium text-gray-700"
                >
                  Email Address <span className="text-red-700">*</span>
                </label>
                <Input
                  id="email"
                  type="email"
                  placeholder="Enter your email address"
                  value={formData.email}
                  onChange={e => handleInputChange('email', e.target.value)}
                  className={cn(
                    (formErrors.email || signInUrl) && 'border-red-500 focus-visible:ring-red-500',
                  )}
                  aria-invalid={!!formErrors.email || !!signInUrl}
                  aria-describedby={formErrors.email || signInUrl ? 'email-error' : undefined}
                  disabled={isSubmitting}
                  autoComplete="email"
                />
                {(formErrors.email || signInUrl) && (
                  <p
                    id="email-error"
                    className="mt-1 text-sm text-red-700"
                  >
                    {signInUrl ? (
                      <>
                        This email address already has an assessment linked to it. Please use a
                        different email address, or{' '}
                        <Button
                          asChild
                          variant="link"
                          className="text-primary underline! h-auto p-0 align-baseline text-sm font-semibold underline-offset-2"
                        >
                          <a href={signInUrl}>Sign In</a>
                        </Button>
                        {' to continue.'}
                      </>
                    ) : (
                      formErrors.email
                    )}
                  </p>
                )}
              </div>

              {advisor?.consumerSmsMfaRequired && (
                <PhoneNumberField
                  id="phoneNumber"
                  name="client-phone-manual-entry"
                  value={{
                    countryCode: formData.phoneCountryCode,
                    phoneNumber: formData.phoneNumber,
                  }}
                  onChange={{
                    countryCode: countryCode => {
                      setFormData(current => ({
                        ...current,
                        phoneCountryCode: countryCode,
                        phoneNumber: formatByCountry(stripDigits(current.phoneNumber), countryCode),
                      }));
                      setFormErrors(current => ({ ...current, phoneNumber: undefined }));
                    },
                    phoneNumber: value => {
                      setFormData(current => ({
                        ...current,
                        phoneNumber: formatByCountry(stripDigits(value), current.phoneCountryCode),
                      }));
                      setFormErrors(current => ({ ...current, phoneNumber: undefined }));
                    },
                  }}
                  validation={{
                    invalid: Boolean(formErrors.phoneNumber),
                    error:
                      (isPhoneTouched || formErrors.phoneNumber) && formErrors.phoneNumber ? (
                        <p className="text-sm text-red-700">{formErrors.phoneNumber}</p>
                      ) : undefined,
                    onBlur: () => {
                      setIsPhoneTouched(true);
                      const error = validateForCountry(
                        stripDigits(formData.phoneNumber),
                        formData.phoneCountryCode,
                      );
                      setFormErrors(current => ({
                        ...current,
                        phoneNumber: error ?? undefined,
                      }));
                    },
                  }}
                  options={{ disabled: isSubmitting, required: true, showHelper: false }}
                />
              )}

              {submitError && (
                <div className="rounded-lg bg-red-50 p-3 text-sm text-red-600">{submitError}</div>
              )}

              <Button
                type="submit"
                className="mt-2 w-full rounded-full py-6 text-base font-medium"
                disabled={isSubmitting || hasExistingAssessmentError || !isMfaFormComplete}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Processing...
                  </>
                ) : (
                  'Start Assessment'
                )}
              </Button>
            </form>

            <p className="mx-auto mt-4 max-w-[75%] text-left text-xs text-gray-700">
              By continuing, you consent to receive communications related to your long-term care
              planning assessment.
            </p>
          </div>
        </div>
      </main>

      <footer className="py-4 text-center text-sm text-gray-700"></footer>
    </div>
  );
}
